/**
 * ExpandedLegCard Component
 * Detailed leg card with player info, prop details, and progress bar
 */

import LegProgressBar from '@components/ui/LegProgressBar';
import type { TrackedParlayLeg } from '@hooks/useTrackedParlays';
import { isTeamBet } from '@parlay-picker/shared/correlationUtils';
import { formatGameClock } from '@parlay-picker/shared/formatting';
import type { LiveGameScores, LiveLegStats } from '@parlay-picker/shared/hooks';
import { legTextWithMarket } from '@parlay-picker/shared/statLabels';
import { getEffectiveStatType } from '@parlay-picker/shared/statType';
import { getTeamAbbr, getTeamLogo } from '@parlay-picker/shared/teams';
import { motion } from 'framer-motion';
import type React from 'react';
import { formatPropDisplay, usePlayerHeadshotUrl } from '../utils';

// =============================================================================
// Types
// =============================================================================

interface ExpandedLegCardProps {
  leg: TrackedParlayLeg;
  liveStats: LiveLegStats | null;
  gameScores: LiveGameScores | null;
  isLive: boolean;
  /** Whether the game has stats data (live OR final) - for progress bars */
  hasLiveData: boolean;
  isConnected: boolean;
  /** Live DNP detection (player not in box score for live/final game) */
  liveDnp?: boolean;
  /** Index for staggered entrance animation */
  legIndex?: number;
}

// =============================================================================
// Helpers
// =============================================================================

/**
 * Check if leg is a total prop
 */
function isTrackedLegTotal(leg: TrackedParlayLeg): boolean {
  const statType = leg.statType?.toLowerCase() || '';
  return statType === 'total' || statType === 'totals';
}

/**
 * Extract the bet team name from the prop string for ML/spread legs.
 * e.g., "Memphis Grizzlies ML" → "Memphis Grizzlies"
 *       "Memphis Grizzlies +3" → "Memphis Grizzlies"
 *       "Grizzlies -5.5" → "Grizzlies"
 * Returns null if no team name can be extracted.
 */
function extractBetTeamFromProp(prop: string | undefined | null): string | null {
  if (!prop) return null;
  // Strip trailing " ML" (moneyline label)
  let cleaned = prop.replace(/\s+ML$/i, '').trim();
  // Strip trailing signed number (spread line like "+3" or "-5.5")
  cleaned = cleaned.replace(/\s+[+-]?\d+(\.\d+)?$/, '').trim();
  return cleaned || null;
}

/**
 * Check if leg is eligible for progress bar
 */
function isProgressBarEligible(leg: TrackedParlayLeg): boolean {
  if (leg.dnp) return false;
  if (leg.player && !isTeamBet(leg.statType)) return true;
  if (isTrackedLegTotal(leg)) return true;
  return false;
}

/**
 * Format the prop display for a leg
 */
function formatTrackedLegProp(leg: TrackedParlayLeg): string {
  const prop = leg.prop || '';
  if (/\d/.test(prop)) {
    return legTextWithMarket(formatPropDisplay(prop) || prop, leg.marketKey);
  }
  const parts: string[] = [];
  if (leg.overUnder) parts.push(leg.overUnder);
  if (leg.line != null) parts.push(String(leg.line));
  if (prop) parts.push(formatPropDisplay(prop) || prop);
  return legTextWithMarket(parts.join(' ') || prop, leg.marketKey);
}

// =============================================================================
// Component
// =============================================================================

export function ExpandedLegCard({
  leg,
  liveStats,
  gameScores,
  isLive,
  hasLiveData: _hasLiveData,
  isConnected,
  liveDnp,
  legIndex = 0,
}: ExpandedLegCardProps): React.ReactElement {
  const isTotal = isTrackedLegTotal(leg);
  const isPlayerProp = leg.player && !isTeamBet(leg.statType);

  // Prefer leg.actual when available — SSE data can be stale or stat type unmapped
  // For settled legs without SSE, synthesize stats from DB actual value
  const effectiveLiveStats =
    liveStats && leg.actual != null
      ? { ...liveStats, currentStat: leg.actual }
      : (liveStats ??
        (leg.actual != null
          ? {
              currentStat: leg.actual,
              quarter: null,
              timeRemaining: null,
              gameStatus: 'final' as const,
            }
          : null));
  const liveClock = formatGameClock(effectiveLiveStats);

  // For settled totals without SSE, synthesize gameScores from DB actual value
  const effectiveGameScores: LiveGameScores | null =
    gameScores ??
    (isTotal && leg.actual != null
      ? {
          homeScore: leg.actual,
          awayScore: 0,
          quarter: null,
          timeRemaining: null,
          gameStatus: 'final' as const,
        }
      : null);

  const hasLiveStatsForLeg = isTotal ? effectiveGameScores !== null : effectiveLiveStats !== null;
  const showProgress =
    isProgressBarEligible(leg) && ((isConnected && hasLiveStatsForLeg) || leg.actual != null);

  // Show DNP if settled with dnp=true OR detected live
  const showDnp = leg.dnp || liveDnp === true;

  // For ML/spread, the bet team comes from leg.prop (leg.team is away team by server convention)
  const isMLOrSpread =
    !isTotal &&
    !isPlayerProp &&
    isTeamBet(leg.statType) &&
    leg.statType !== 'total' &&
    leg.statType !== 'totals';
  const betTeamName = isMLOrSpread ? extractBetTeamFromProp(leg.prop) : null;

  // Get player/team info for display
  const leagueKey = leg.leagueKey;
  const headshotUrl = usePlayerHeadshotUrl(isPlayerProp ? leg.player : null, leagueKey);
  const displayTeam = betTeamName || leg.team;
  const teamLogo = displayTeam ? getTeamLogo(displayTeam, leagueKey) : '';
  const opponentLogo = leg.opponentTeam ? getTeamLogo(leg.opponentTeam, leagueKey) : '';

  // Build matchup string
  const matchupString =
    leg.team && leg.opponentTeam
      ? `${getTeamAbbr(leg.team, leagueKey)} vs ${getTeamAbbr(leg.opponentTeam, leagueKey)}`
      : leg.team
        ? getTeamAbbr(leg.team, leagueKey)
        : '';

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 } as const}
      animate={{ opacity: 1, x: 0 } as const}
      transition={{ duration: 0.25, delay: legIndex * 0.05 }}
      className={`bg-theme-surface/30 rounded-lg p-3 border ${
        showDnp
          ? 'border-theme-warning/30'
          : leg.hit === true
            ? 'border-theme-success/30'
            : leg.hit === false
              ? 'border-theme-danger/30'
              : 'border-theme-border/30'
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Player headshot or team logo */}
        <div className="shrink-0">
          {isPlayerProp && headshotUrl ? (
            <div className="w-12 h-12 rounded-full overflow-hidden bg-theme-surface">
              <img
                src={headshotUrl}
                alt={leg.player || 'Player'}
                className="w-full h-full object-cover object-top"
                onError={(e) => {
                  // Fallback to initials on error
                  (e.target as HTMLImageElement).style.display = 'none';
                  (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                }}
              />
              <div className="hidden w-full h-full items-center justify-center text-sm font-bold text-theme-text-secondary">
                {(leg.player || '')
                  .split(' ')
                  .map((n) => n[0])
                  .join('')
                  .slice(0, 2)}
              </div>
            </div>
          ) : isTotal && teamLogo && opponentLogo ? (
            <div className="flex items-center -space-x-2">
              <img
                src={teamLogo}
                alt=""
                loading="lazy"
                decoding="async"
                className="w-8 h-8 object-contain"
              />
              <img
                src={opponentLogo}
                alt=""
                loading="lazy"
                decoding="async"
                className="w-8 h-8 object-contain"
              />
            </div>
          ) : teamLogo ? (
            <img
              src={teamLogo}
              alt=""
              loading="lazy"
              decoding="async"
              className="w-10 h-10 object-contain"
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-theme-surface flex items-center justify-center">
              <span className="text-xs font-bold text-theme-text-tertiary">
                {isPlayerProp
                  ? (leg.player || '')
                      .split(' ')
                      .map((n) => n[0])
                      .join('')
                      .slice(0, 2)
                  : '?'}
              </span>
            </div>
          )}
        </div>

        {/* Main content */}
        <div className="flex-1 min-w-0">
          {/* Player/Team name + Result indicator */}
          <div className="flex items-center gap-2">
            {showDnp ? (
              <span className="text-theme-warning" title="Did Not Play">
                ⊘
              </span>
            ) : leg.hit === true ? (
              <span className="text-theme-success text-lg">✓</span>
            ) : leg.hit === false ? (
              <span className="text-theme-danger text-lg">✗</span>
            ) : null}
            <span className={`font-medium truncate text-theme-text ${showDnp ? 'italic' : ''}`}>
              {leg.player || (isTotal ? 'Game Total' : displayTeam || 'Unknown')}
            </span>
            {showDnp && <span className="text-xs text-theme-warning">(DNP)</span>}
          </div>

          {/* Matchup */}
          {matchupString && (
            <div className="text-xs text-theme-text-tertiary mt-0.5">{matchupString}</div>
          )}

          {/* Prop + Odds row */}
          <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5 mt-2">
            <span className="text-sm text-theme-text min-w-0">{formatTrackedLegProp(leg)}</span>
            {showDnp ? (
              <span className="flex items-center gap-1.5">
                <span className="text-xs text-theme-text-tertiary line-through">{leg.odds}</span>
                <span className="text-xs font-medium text-theme-warning">VOID</span>
              </span>
            ) : (
              <span className="text-sm font-medium text-theme-text-secondary">{leg.odds}</span>
            )}
          </div>

          {/* Progress bar for live games */}
          {showProgress && leg.line != null && leg.overUnder && (
            <div className="mt-2">
              {isTotal ? (
                <LegProgressBar
                  type="total"
                  line={leg.line}
                  overUnder={leg.overUnder as 'Over' | 'Under'}
                  gameScores={effectiveGameScores}
                  settled={leg.settled}
                  hit={leg.hit}
                />
              ) : (
                <LegProgressBar
                  type="player"
                  line={leg.line}
                  overUnder={leg.overUnder as 'Over' | 'Under'}
                  statType={getEffectiveStatType(leg)}
                  liveStats={effectiveLiveStats}
                  settled={leg.settled}
                  hit={leg.hit}
                />
              )}
            </div>
          )}

          {/* Current stat value for live games */}
          {isLive && effectiveLiveStats && isPlayerProp && (
            <div className="mt-1 text-xs text-theme-text-tertiary flex flex-wrap items-baseline gap-x-1">
              Current:{' '}
              {leg.line != null ? (
                <span
                  className={`font-bold text-sm ${
                    effectiveLiveStats.currentStat >= leg.line
                      ? leg.overUnder === 'Over'
                        ? 'text-theme-success'
                        : 'text-theme-danger'
                      : leg.overUnder === 'Under'
                        ? 'text-theme-success'
                        : 'text-theme-primary'
                  }`}
                >
                  {effectiveLiveStats.currentStat}
                </span>
              ) : (
                <span className="text-theme-text font-medium">
                  {effectiveLiveStats.currentStat}
                </span>
              )}
              {leg.line != null && (
                <span className="text-theme-text-tertiary ml-1">/ {leg.line}</span>
              )}
              {liveClock && <span className="ml-2">({liveClock})</span>}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
